import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  linkedSignal,
  OnInit,
  Output,
  Signal,
  signal,
  ViewChild,
} from '@angular/core';
import { forkJoin, from, of, switchMap } from 'rxjs';
import { ErrorService } from 'src/app/services/error.service';
import { DeviceCreationComponent } from '../../../components/lists/sb-device-list/device-creation/device-creation.component';
import {
  AddressDTO,
  API,
  AttributeMapDTO,
  AttributeType,
  ChannelFullDTO,
  CompanyFullDTO,
  DeviceDTO,
  DeviceFullDTO,
  DeviceOnlineStatus,
  LocationFullDTO,
  Order,
  OwnedScreenDTO,
  PoolDTO,
  ReportingFullDTO,
  ss_nm_Device_export_filter,
  ss_nm_Device_export_order,
  ss_nm_Device_retrieveAll_filter,
  ss_nm_Device_retrieveAll_order,
} from '../../../services/api/api';
import { UtilService } from '../../../services/util.service';
import { Point, Polygon } from 'geojson';
import { GeoUtilService } from 'src/app/services/geo/geoutil.service';
import { TranslateService } from '@ngx-translate/core';
import { TagService } from '../../../services/tag.service';
import { DeviceFullDTOAndTags } from '../../../models/taggable.model';
import { NavigationService } from '@ngx-seatback/utils';
import { ModalNavigationComponent } from '../../../services/gui/modal/modal-navigation-component';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import {
  IDAndTypeInterface,
  OrderOption,
  SbDeviceListColumn,
  SbSplitButton,
  SelectionState,
} from '../../../services/global/global-interfaces';
import { SbDeviceOptionsComponent } from '../../../components/lists/sb-device-list/sb-device-options/sb-device-options.component';
import { ReportingModalComponent } from '../../../components/standard-reporting/reporting-modal/reporting-modal.component';
import { ReportingService } from '../../../services/reporting.service';
import { PermissionService } from '../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { createAttributeColumns, getPropsFromDTO, isColumnMatch } from '../../../services/global/global-methods';
import { clientColumnDefaultsDevices } from './clientDefaultColumnDevices';
import { DynamicTableBase } from '../../../components/dynamic-table-elements/DynamicTableBase';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelComponent } from '@ngx-seatback/layout';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { MultiSelectButtonAction } from '../../../components/table-multi-select-button/table-multi-select-button.component';

type SplitButtonAction = 'options';
type DeviceFullDTOAndTagsAndButtons = DeviceFullDTOAndTags & SbSplitButton<SplitButtonAction>;

@Component({
  selector: 'sb-page-devices',
  templateUrl: './devices.component.html',
  styleUrls: ['./devices.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DevicesComponent extends DynamicTableBase<SbDeviceListColumn> implements OnInit, AfterViewInit, ModalNavigationComponent {
  // injects

  public readonly api = inject(API);
  public readonly util = inject(UtilService);
  private readonly geoUtil = inject(GeoUtilService);
  private readonly errorHandler = inject(ErrorService);
  public readonly translate = inject(TranslateService);
  public readonly tagService = inject(TagService);
  private readonly navService = inject(NavigationService);
  private readonly modalNavigation = inject(ModalNavigationService);
  private readonly reportingService = inject(ReportingService);
  public readonly permissionService = inject(PermissionService);
  // global reference imports
  protected readonly window = window;
  // ViewChildren
  @ViewChild('FilterPanelRef') filterPanelRef: FilterPanelComponent;

  // Inputs
  @Input() isInModalContext: boolean = false;
  @Input() selectionMode: boolean = false;

  @Output() elementSelected: EventEmitter<DeviceDTO> = new EventEmitter<DeviceDTO>();

  /* filter variables */
  radiusValue: number = 11;
  // search
  search: string | null = null;
  exactMatch: SearchOptions = 'contains';
  // Id filtering
  filteredLocations: LocationFullDTO[] = [];
  filteredPools: PoolDTO[] = [];
  filteredChannels: ChannelFullDTO[] = [];
  filteredCulls: number[] = [];
  filteredTags: number[] = [];
  // ThreeWay-toggles
  paired: boolean | null = null;
  productive: boolean | null = null;
  noSIM: boolean | null = null;
  tempDisabled: boolean | null = null;
  external: boolean | null = null;
  hasServiceRequest: boolean | null = null;
  hasPool: boolean | null = null;
  hasLocation: boolean | null = null;
  // Device Online Status
  online: boolean = true;
  currentlyOffline: boolean = true;
  plannedOffline: boolean = true;
  offline: boolean = true;
  unknown: boolean = true;

  /* ordering */
  orderOption = signal<OrderOption<SbDeviceListColumn>>({
    direction: Order.asc,
    value: {
      id: 'identifier',
      title: 'lang.globals.identifier',
      selected: false,

      type: 'PROPERTY',
      orderId: ss_nm_Device_retrieveAll_order.IDENTIFIER,
      canOrder: true,
      filterId: ss_nm_Device_retrieveAll_filter.IDENTIFIER,
      canFilter: true,
    },
  });
  filterPanelOrderOption = linkedSignal<OrderOption<SbDeviceListColumn>, OrderOption<SbDeviceListColumn>>({
    source: this.orderOption,
    computation: orderOption => {
      return { ...orderOption };
    },
  });
  allOrderOptions: OrderOption<SbDeviceListColumn>[] = [];

  // export
  isSelectedDevicesExport: boolean = false;
  exportLoading = false;
  form = new FormGroup({
    Title: new FormControl('', [Validators.required]),
  });

  /* global */
  showSideBar: boolean = false;
  devices: DeviceFullDTOAndTagsAndButtons[] = [];
  selectedDevices = signal<Map<number, DeviceFullDTOAndTagsAndButtons>>(new Map());
  selectionState: Signal<SelectionState> = computed(() => {
    const currentlySelectedItems = this.selectedDevices();
    if (!this.devices || this.devices.length === 0) {
      return 'none';
    }
    const allSelectedOnCurrentPage = this.devices.every(item => currentlySelectedItems.has(item.id));
    if (allSelectedOnCurrentPage) {
      return 'all';
    }
    const someSelectedOnCurrentPage = this.devices.some(item => currentlySelectedItems.has(item.id));
    if (someSelectedOnCurrentPage) {
      return 'partly';
    }
    return 'none';
  });

  constructor() {
    super();
    // has to be called before resetOrderOption to initialize columns
    this.columnDefaults = clientColumnDefaultsDevices[this.util.getSeatbackCMSClient()] ?? clientColumnDefaultsDevices['generic'] ?? [];
    this.resetOrderOption();
  }

  ngOnInit() {
    if (this.isInModalContext) {
      this.selectedDevices = this.modalNavigation.getCurrentNavigation()?.data?.selectedDevices;
    }

    const attributeTypes: AttributeType[] = [];
    if (this.permissionService.hasPermission(this.permissionService.Permission.device_attributes_show)) {
      attributeTypes.push(AttributeType.Device);
    }
    if (this.permissionService.hasPermission(this.permissionService.Permission.location_attributes_show)) {
      attributeTypes.push(AttributeType.Location);
    }
    if (this.permissionService.hasPermission(this.permissionService.Permission.company_attributes_show)) {
      attributeTypes.push(AttributeType.Company);
    }
    of(attributeTypes)
      .pipe(
        switchMap((types: AttributeType[]) =>
          forkJoin({
            attributeMap: this.api.shared_Attribute_retrieve({ type: types }),
            preselectedColumns: from(this.loadSelectedColumns('deviceListSelectedColumnsV1')),
          })
        ),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (forkJoinResult: { attributeMap: AttributeMapDTO; preselectedColumns: IDAndTypeInterface[] | null }) => {
          this.initColumns(forkJoinResult.attributeMap, forkJoinResult.preselectedColumns);
          this.requestDebounced();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  resetOrderOption() {
    // reset to default ordering, copy object properties to avoid changing the original default
    this.orderOption.set({
      value: this.columns.find(col => col.id === 'identifier'),
      direction: Order.asc,
    });
  }

  ngAfterViewInit() {
    // subscription for filterValue deselect
    this.initTagSubscription();
  }

  setToOrderAndRefresh(pValue: SbDeviceListColumn) {
    this.setToOrderOption(pValue);
    this.requestDebounced();
  }

  private setToOrderOption(pValue: SbDeviceListColumn) {
    if (!pValue) {
      // nothing is set fallback to default
      this.resetOrderOption();
      return;
    }
    const currentOrderOption = this.orderOption();
    if (currentOrderOption.value === pValue) {
      if (currentOrderOption.direction === Order.asc) {
        this.orderOption.set({ ...currentOrderOption, direction: Order.desc });
      } else {
        this.orderOption.set({ ...currentOrderOption, direction: Order.asc });
      }
    } else {
      this.orderOption.set({ value: pValue, direction: Order.asc });
    }
  }

  isFilterClassActive(column: SbDeviceListColumn): boolean {
    if (column.type === 'PROPERTY') {
      const id = column.id;
      const canFilter = column?.canFilter;

      const propertyFilters = {
        pool: this.filteredPools?.length > 0,
        location: this.filteredLocations?.length > 0,
        channel: this.filteredChannels?.length > 0,
        default: canFilter && this.columnFiltersMap?.has(id),
      };

      return propertyFilters[id] ?? propertyFilters.default;
    }

    const filterValue = this.columnFiltersMap?.get(column.id);

    return this.stringTransformation.stringNotNull(filterValue?.value);
  }

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const options = this.getRequestOptions();
    options.withinPolygon = this.calculateRadius();
    const sub = this.api
      .ss_nm_Device_retrieveAll(options as any, this.pagination)
      .pipe(
        take(1),
        switchMap(devices => this.tagService.getAndAddTagEntitiesObs(this.tagService.TaggableDTO.Device, devices)),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (e: DeviceFullDTOAndTags[]) => {
          this.devices = this.buildSplitButtons(e);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  export() {
    this.loading.set(true);
    this.exportLoading = true;
    this.showSideBar = false;
    const requestOptions = this.getRequestOptions() as any;
    if (this.isSelectedDevicesExport && this.selectedDevices().size > 0) {
      requestOptions.id = Array.from(this.selectedDevices().keys());
    }
    const reportName = this.form.get('Title').value;
    if (reportName) {
      requestOptions.reportName = reportName;
    }
    this.api
      .ss_nm_Device_export(requestOptions)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rep: ReportingFullDTO) => {
          this.reportingService.addReport(rep);
          this.modalService
            .showNavigationModal({
              entryComponent: {
                value: ReportingModalComponent,
                data: rep,
              },
              title: this.translate.instant('lang.globals.download'),
              modalOptions: { windowClass: 'M' },
            })
            .then(() => {})
            .catch(() => {})
            .finally(() => {
              this.clearExportModal();
            });
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  clearExportModal() {
    this.loading.set(false);
    this.isSelectedDevicesExport = false;
    this.exportLoading = false;
  }

  getFilterOptions(): { by: ss_nm_Device_retrieveAll_filter; param?: string; values: string[] }[] | null {
    const ret: { by: ss_nm_Device_retrieveAll_filter; param?: string; values: string[] }[] = [];

    if (this.isFilterEnabled()) {
      for (const [columnId, columnFilterValue] of this.columnFiltersMap.entries()) {
        switch (columnFilterValue?.column?.type) {
          case 'DEVICE_ATTRIBUTE':
            ret.push({
              by: ss_nm_Device_retrieveAll_filter.ATTRIBUTES,
              param: columnId,
              values: ['%' + columnFilterValue.value + '%'],
            });
            break;
          case 'LOCATION_ATTRIBUTE':
            ret.push({
              by: ss_nm_Device_retrieveAll_filter.LOCATION_ATTRIBUTES,
              param: columnId,
              values: ['%' + columnFilterValue.value + '%'],
            });
            break;
          case 'COMPANY_ATTRIBUTE':
            ret.push({
              by: ss_nm_Device_retrieveAll_filter.LOCATION_MANAGED_BY_ATTRIBUTES,
              param: columnId,
              values: ['%' + columnFilterValue.value + '%'],
            });

            break;
          case 'PROPERTY':
            if (columnFilterValue?.column?.filterId) {
              ret.push({
                by: columnFilterValue?.column?.filterId,
                values: ['%' + columnFilterValue.value + '%'],
              });
            }
            break;
        }
      }
      return ret.length > 0 ? ret : null;
    }

    return null;
  }

  isFilterEnabled(): boolean {
    return this.columnFiltersMap && this.columnFiltersMap.size > 0;
  }

  getOrderOptions(): { by: ss_nm_Device_retrieveAll_order; param?: string; direction: Order } {
    // if no orderOption -> return default order
    const currentOrderOption = this.orderOption();
    if (!currentOrderOption.value?.type) {
      this.resetOrderOption();
      return {
        by: ss_nm_Device_retrieveAll_order.IDENTIFIER,
        direction: Order.asc,
      };
    }

    // if value equals column title
    switch (currentOrderOption.value.type) {
      case 'DEVICE_ATTRIBUTE':
        return {
          by: ss_nm_Device_retrieveAll_order.ATTRIBUTES,
          param: currentOrderOption.value.id,
          direction: currentOrderOption.direction,
        };

      case 'LOCATION_ATTRIBUTE':
        return {
          by: ss_nm_Device_retrieveAll_order.LOCATION_ATTRIBUTES,
          param: currentOrderOption.value.id,
          direction: currentOrderOption.direction,
        };
      case 'COMPANY_ATTRIBUTE':
        return {
          by: ss_nm_Device_retrieveAll_order.LOCATION_MANAGED_BY_ATTRIBUTES,
          param: currentOrderOption.value.id,
          direction: currentOrderOption.direction,
        };
      case 'PROPERTY':
        return {
          by: currentOrderOption.value.orderId,
          direction: currentOrderOption.direction,
        };
      default:
        this.resetOrderOption();
        return {
          by: ss_nm_Device_retrieveAll_order.IDENTIFIER,
          direction: Order.asc,
        };
    }
  }

  getRequestOptions(): {
    productive?: boolean;
    offline?: boolean;
    external?: boolean;
    disabled?: boolean;
    hasServiceRequest?: boolean;
    onlineStatus?: DeviceOnlineStatus[];
    order?: { by: ss_nm_Device_retrieveAll_order | ss_nm_Device_export_order; param?: string; direction: Order };
    filter?: {
      by: ss_nm_Device_retrieveAll_filter | ss_nm_Device_export_filter;
      param?: string;
      values: string[];
    }[];
    paired?: boolean;
    channel?: number[];
    pool?: number[];
    hasPool?: boolean;
    location?: number[];
    hasLocation?: boolean;
    playerCull?: number[];
    tag?: number[];
    search?: string;
    withinPolygon?: Polygon[];
    id?: number[];
  } {
    return {
      productive: this.productive,
      offline: this.noSIM,
      external: this.external,
      disabled: this.tempDisabled,
      hasServiceRequest: this.hasServiceRequest,
      paired: this.paired,
      hasLocation: this.hasLocation,
      hasPool: this.hasPool,
      // online: this.online,
      onlineStatus: this.getDeviceOnlineStatus(),
      order: this.getOrderOptions(),
      filter: this.getFilterOptions(),
      pool: this.filteredPools?.map(item => item.id) ?? null,
      location: this.filteredLocations?.map(item => item.id) ?? null,
      channel: this.filteredChannels?.map(item => item.id) ?? null,
      search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
      playerCull: this.filteredCulls,
      tag: this.filteredTags,
    };
  }

  calculateRadius(): Polygon[] {
    if (this.radiusValue === 11) {
      return null;
    } else {
      const point: Point = this.geoUtil.coordinatesToPoint({
        x: this.userService.userGeoLocation.coords.longitude,
        y: this.userService.userGeoLocation.coords.latitude,
      });
      const circle: Polygon = this.geoUtil.circle(point, this.radiusValue * 1000);
      return [circle];
    }
  }

  getDeviceOnlineStatus(): DeviceOnlineStatus[] {
    const arr: DeviceOnlineStatus[] = [];

    if (this.online) {
      arr.push(DeviceOnlineStatus.Online);
    }
    if (this.currentlyOffline) {
      arr.push(DeviceOnlineStatus.CurrentlyOffline);
    }
    if (this.plannedOffline) {
      arr.push(DeviceOnlineStatus.PlannedOffline);
    }
    if (this.offline) {
      arr.push(DeviceOnlineStatus.Offline);
    }
    if (this.unknown) {
      arr.push(DeviceOnlineStatus.Unknown);
    }

    return arr;
  }

  initColumns(attributeMap: AttributeMapDTO, preselectedColumns: IDAndTypeInterface[] | null) {
    this.columns = [];

    const allColumns: string[] = [
      ...getPropsFromDTO('', Object.keys(new DeviceFullDTO())),
      ...getPropsFromDTO('location.address', Object.keys(new AddressDTO())),
      ...getPropsFromDTO('location', Object.keys(new LocationFullDTO())),
      ...getPropsFromDTO('location.managedBy.address', Object.keys(new AddressDTO())),
      ...getPropsFromDTO('location.managedBy', Object.keys(new CompanyFullDTO())),
      ...getPropsFromDTO('screens', Object.keys(new OwnedScreenDTO())),
    ];

    for (const col of allColumns) {
      const theCol = this.columnDefaults.find(defCol => defCol.id === col);
      if (theCol) {
        this.columns.push({ ...theCol });
      } else if (!col.startsWith('_')) {
        this.columns.push({ id: col, title: col, selected: false, type: 'PROPERTY' });
      }
    }

    this.columns.push(
      ...createAttributeColumns(attributeMap.deviceAttributes, 'DEVICE_ATTRIBUTE'),
      ...createAttributeColumns(attributeMap.locationAttributes, 'LOCATION_ATTRIBUTE'),
      ...createAttributeColumns(attributeMap.companyAttributes, 'COMPANY_ATTRIBUTE')
    );

    if (preselectedColumns) {
      // if there are preselectedColumns by the user, then Ids should be matching and those shall be selected
      preselectedColumns.forEach(preCol => {
        const matchingCol = this.columns.find(col => isColumnMatch(col, preCol));
        if (matchingCol) {
          matchingCol.selected = true;
          this.selectedColumns.push(matchingCol);
        }
      });
    } else {
      this.selectedColumns = this.columns.filter(column => column.selected);
    }

    this.initColumnFilterMap('deviceFilter');

    this.applyColumns();
  }

  applyColumns() {
    this.allOrderOptions = this.refreshAllOrderOptions();
    this.saveSelectedColumns('deviceListSelectedColumnsV1');
    this.refreshColumnFilters();
  }

  createDevice() {
    this.modalService
      .showNavigationModal({
        entryComponent: {
          value: DeviceCreationComponent,
          data: {
            pool: this.getFirstElementOfArray<PoolDTO>(this.filteredPools),
            channel: this.getFirstElementOfArray<ChannelFullDTO>(this.filteredChannels),
            location: this.getFirstElementOfArray<LocationFullDTO>(this.filteredLocations),
            useParamData: true,
          },
        },
        title: this.translate.instant('lang.devices.createDevice'),
        icon: 'plus',
        // modalOptions: {size: 'sm'}
      })
      .then((e: DeviceDTO) => {
        if (e) {
          this.navService.navigateToPoint(
            this.util.RoutingID.deviceDetails,
            [
              {
                id: 'deviceID',
                value: e.id,
              },
            ],
            {
              queryParams: { returnTo: this.util?.getPageLink() },
            }
          );
        }
      })
      .catch(() => {});
  }

  getFirstElementOfArray<T>(arr: T[]): T | null {
    return arr?.length > 0 ? arr[0] : null;
  }

  selectDevice(item: DeviceFullDTOAndTags) {
    const currentlySelectedItems = this.selectedDevices();
    if (currentlySelectedItems.has(item.id)) {
      currentlySelectedItems.delete(item.id);
      this.selectedDevices.set(new Map(currentlySelectedItems));
    } else {
      currentlySelectedItems.set(item.id, item);
      this.selectedDevices.set(new Map(currentlySelectedItems));
    }
  }

  returnSelectedDevices() {
    this.modalNavigation.result(Array.from(this.selectedDevices().values()));
  }

  deselectDevices() {
    this.selectedDevices.set(new Map());
  }

  showOptionsModal(pDevice: DeviceFullDTOAndTags) {
    this.modalService
      .showNavigationModal({
        entryComponent: { value: SbDeviceOptionsComponent, data: { device: pDevice } },
        title: this.translate.instant('lang.globals.device') + ' ' + pDevice.id,
        icon: 'settings',
        modalOptions: { size: 'lg' },
      })
      .then(() => {})
      .catch(() => {})
      .finally(() => {
        this.requestDebounced();
      });
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.updateColumnFilters();
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    // property filter
    this.paired = filterMap.get('paired')?.[0];
    this.noSIM = filterMap.get('noSIM')?.[0];
    this.productive = filterMap.get('productive')?.[0];
    this.tempDisabled = filterMap.get('tempDisabled')?.[0];
    this.external = filterMap.get('external')?.[0];
    this.hasServiceRequest = filterMap.get('service')?.[0];
    this.hasPool = filterMap.get('hasPool')?.[0];
    this.hasLocation = filterMap.get('hasLocation')?.[0];
    // status filter
    this.online = filterMap.get('online')?.[0];
    this.currentlyOffline = filterMap.get('currentlyOffline')?.[0];
    this.plannedOffline = filterMap.get('plannedOffline')?.[0];
    this.offline = filterMap.get('offline')?.[0];
    this.unknown = filterMap.get('unknown')?.[0];
    // Id filter
    this.filteredLocations = filterMap.get('locations');
    this.filteredPools = filterMap.get('pools');
    this.filteredChannels = filterMap.get('channels');
    this.filteredTags = filterMap.get('tags')?.map(item => item.id);
    this.filteredCulls = filterMap.get('playerCulls')?.map(item => item.id);
    // search
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }

  private updateColumnFilters(): void {
    // order
    this.orderOption.set({ ...this.filterPanelOrderOption() });
    // columnFilter
    if (this.removableColumnFilters?.length > 0) {
      this.removableColumnFilters.forEach(filter => this.columnFiltersMap.delete(filter));
    }
    if (this.isInModalContext) {
      return;
    }
    this.saveColumnFilterToQueryParam('deviceFilter');
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.device_show])) {
        item.sbButtons.push({
          icon: 'details',
          tooltip: 'lang.globals.details',
          link: RoutingID.deviceDetails,
          pathParams: [{ id: 'deviceID', value: item?.id }],
          queryParams: { returnTo: this.util.getPageLink() },
        });
      }
      if (
        this.permissionService.hasPermissions([
          this.permissionService.Permission.device_show,
          this.permissionService.Permission.device_pool_edit,
          this.permissionService.Permission.device_location_edit,
          this.permissionService.Permission.device_properties_edit,
        ])
      ) {
        item.sbButtons.push({
          icon: 'setting',
          tooltip: 'lang.globals.options',
          action: 'options',
        });
      }
    });
    return result;
  }

  resolveSplitButtonAction(device: DeviceFullDTOAndTagsAndButtons, $event: SplitButtonAction) {
    if ($event === 'options') {
      this.showOptionsModal(device);
    } else {
      console.error('Missing action implementation of Splitbutton');
    }
  }

  selectAction($event: MultiSelectButtonAction) {
    if (!this.devices || this.devices.length === 0) {
      return;
    }
    const currentlySelectedDevices = this.selectedDevices();
    switch ($event) {
      case 'selectPage':
        this.devices.forEach(item => {
          currentlySelectedDevices.set(item.id, item);
        });
        this.selectedDevices.set(new Map(currentlySelectedDevices));
        break;
      case 'deselectAll':
        this.selectedDevices.set(new Map());
        break;
      case 'deselectPage':
      default:
        this.devices.forEach(item => {
          currentlySelectedDevices.delete(item.id);
        });
        this.selectedDevices.set(new Map(currentlySelectedDevices));
        break;
    }
  }

  setToOrderConnection(column: 'Connection') {
    if (!column) {
      // nothing is set fallback to default
      this.resetOrderOption();
    }
    const orderOption = this.orderOption();
    if (orderOption.value?.id === column) {
      if (orderOption.direction === Order.asc) {
        orderOption.direction = Order.desc;
      } else {
        this.resetOrderOption();
      }
    } else {
      const pOrderId = column === 'Connection' ? ss_nm_Device_retrieveAll_order.LAST_ONLINE : undefined;
      this.orderOption.set({
        value: {
          id: column,
          canOrder: true,
          canFilter: false,
          filterId: null,
          type: 'PROPERTY',
          orderId: pOrderId,
          title: 'lang.globals.connection',
          selected: false,
        },
        direction: Order.asc,
      });
    }
    this.requestDebounced();
  }
}
